import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Inject, Injectable, input, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { InformacionComponentesFiles } from '@interface/files_conn_components';
import { NotificacionService } from './notificacion';
import { CookieUtil } from './cookies';
import { Globals } from '@config/global';
import { ActivatedRoute, Router } from '@angular/router';
import alliedBank from './json/allied-banks.json';
import { openCDT } from './interface/open-cdt.interface';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { WidgetRate } from './interface/widget-rate.interface';
import { CheckParametersComponent } from '../modals/check-parameters/check-parameters.component';
import { BankNotAlliedComponent } from '../modals/bank-not-allied/bank-not-allied.component';
import { AlertConfirmComponent } from '../resources-alerts/alert-confirm/alert-confirm.component';
import { ModalPremiarFidelidadComponent } from '../modals/modal-premiar-fidelidad/component/modal-premiar-fidelidad/modal-premiar-fidelidad.component';
import { MONTHS_ES } from '@utils/months.utils';
import { BankCard } from '@interface/bank-card.interface';
import { ModalInvestmentConfirmationComponent } from '../routes/hoy-los-bancos-ofrencen-v2/components/shared/modal/modal-investment-confirmation.component';
import { ModalVideoFrameComponent } from '@modals/modal-video-frame/modal-video-frame.component';
import { GtmService } from '@apiShared/analytics/gtm';
import { GetProcessInterface } from '@interface/get-process-interface';
import { RedirectUsersMarketingResponseI } from '@interface/redirect-users-marketing.interface';

@Injectable({
  providedIn: 'root',
})
export class UtilsMethods_Services {
  public confirmProcessExitModaltext = {
    title: '¿Estás seguro de abandonar el proceso?',
    message:
      'Si abandonas el proceso podrías perder el avance del formulario que no hayas guardado. Confirma que deseas salir del proceso:',
    textButtons: {
      confirm: {
        text: 'Cancelar',
        id: 'select_button_cancel_leave_form',
      },
      cancel: {
        text: 'Confirmar',
        id: 'select_button_confirm_leave_form',
      },
    },
  };

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    public globals_var: Globals,
    public breakpointObserver: BreakpointObserver,
    public cookies: CookieUtil,
    private _gta_events: GtmService,
    private router: Router,
    private route: ActivatedRoute,
    private notificacion_components: NotificacionService,
    private modal: NgbModal,
  ) {}

  /**
   * Validates that the current route matches expected marketing campaign parameters.
   *
   * This method checks if the current URL path is '/ahorro' and verifies that all
   * required query parameters match the expected marketing campaign values for
   * tracking purposes (ref, medio, accion, campaña, director, anuncios, detalle).
   *
   * @returns {boolean} True if both the path and all query parameters match the expected
   * marketing campaign configuration, false otherwise.
   *
   * @example
   * ```typescript
   * // URL: /ahorro?ref=PautaSEM2025V1&medio=Google&accion=Pagina&campaña=PautaSEM&director=MejorCDT&anuncios=Google_Ads&detalle=Registro
   * const isValid = this.validatePautaMarketingParams(); // returns true
   *
   * // URL: /other-path?ref=PautaSEM2025V1
   * const isValid = this.validatePautaMarketingParams(); // returns false
   * ```
   */
  validatePautaMarketingParams(ignorePath: boolean = false, pathWithQueryParams: boolean = true): RedirectUsersMarketingResponseI {
    const expectedPath = '/ahorro';
    const expectedParams: Record<string, string> = {
      ref: 'PautaSEM2025V1',
      medio: 'Google',
      accion: 'Pagina',
      campana: 'PautaSEM',
      director: 'MejorCDT',
      anuncios: 'Google_Ads',
      detalle: 'Registro',
    };

    const currentPath = this.router.url.split('?')[0];
    const queryParams = this.route.snapshot.queryParams;

    const isValidPathMatched =
      (ignorePath || currentPath === expectedPath) &&
      Object.keys(expectedParams).every((key) => queryParams[key] === expectedParams[key]);

    const path = isValidPathMatched ? (pathWithQueryParams ? this.router.url : currentPath) : null;

    return {
      path: path,
      url_valid: isValidPathMatched,
    };
  }

  TriggerEventGA(bank_id: number, tag: string) {
    if (bank_id === 30) {
      this._gta_events.triggerGTMEvent(tag, 'ItemNormalApertureBankComponent');
    }
  }

  /**
   * @description Genera la URL de redirección para bancos no aliados.
   * Normaliza el nombre del banco eliminando espacios, acentos y aplicando minúsculas.
   * @param bank_label - El nombre del banco.
   * @returns La URL de redirección formateada.
   */
  redirectToBankNotAllied(bank_label: string): string {
    if (!bank_label) return '';
    const normalizedName = this.deleteSpace(this.removeAccents(this.replaceNameBanks(bank_label))).toLowerCase();
    return `/cdt-${normalizedName}`;
  }

  /**
   * Devuelve los meses del año actual hasta el mes actual, y si faltan 2 días o menos para el siguiente mes,
   * agrega el siguiente mes.
   * @returns Array de objetos { mes: string, path: string }
   */
  getBestCdtMonthsMenu(): { mes: string; path: string }[] {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth(); // 0-indexed
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const day = today.getDate();
    const meses: { mes: string; path: string }[] = [];

    // Meses desde enero hasta el mes actual
    for (let i = 0; i <= month; i++) {
      meses.unshift({
        mes: `${MONTHS_ES[i][0].toUpperCase()}${MONTHS_ES[i].slice(1)}`,
        path: `/mejores-cdt-${MONTHS_ES[i]}-${year}`,
      });
    }

    // Si falta 1 o menos para el siguiente mes, agregar el siguiente mes
    if (daysInMonth - day <= 1) {
      let nextMonth = month + 1;
      let nextYear = year;
      if (nextMonth > 11) {
        nextMonth = 0;
        nextYear++;
      }
      meses.unshift({
        mes: `${MONTHS_ES[nextMonth][0].toUpperCase()}${MONTHS_ES[nextMonth].slice(1)}`,
        path: `/mejores-cdt-${MONTHS_ES[nextMonth]}-${nextYear}`,
      });
    }
    return meses.reverse();
  }

  openVideo(params: any) {
    if (isPlatformBrowser(this.platformId)) {
      if (params.ga) {
        this._gta_events.triggerGTMEvent(params.ga, 'DefaultComponent');
      }

      let modalOpenVideo: any = this.modal.open(ModalVideoFrameComponent, {
        centered: true,
        size: params.size,
        backdrop: params.backdrop,
      });
      modalOpenVideo.componentInstance.data = params;
    }
  }

  /**
   * @description Abre un modal de confirmación de inversión con los detalles del banco seleccionado.
   * Utiliza el servicio `NgbModal` para crear y mostrar el modal.
   * @param bankCard - El objeto BankCard que contiene la información del banco.
   */
  public openModalInvestmentConfirmation(bankCard: BankCard, term_default: number[]): void {
    this._gta_events.triggerGTMEvent('**ga_abre_cdt_general_old_new', 'AppMejorCDTComponent');
    this._gta_events.triggerGTMEvent('ga_astro_btn_abre_cdt_general', 'AppMejorCDTComponent');
    this._gta_events.triggerGTMEvent(`ga_astro_btn_abre_cdt_${bankCard.bank_id}`, 'AppMejorCDTComponent');

    // Filtrar las opciones para remover términos de 90 y 120 días si existen
    if (bankCard.options && Array.isArray(bankCard.options)) {
      bankCard.options = bankCard.options.filter((option) => option.term !== 90 && option.term !== 120);
    }

    const modalRef = this.modal.open(ModalInvestmentConfirmationComponent, {
      centered: true,
      backdrop: 'static',
      size: 'md',
      windowClass: 'modal-dialog-fullscreen-mobile',
    });

    modalRef.componentInstance.dataBank = bankCard;
    modalRef.componentInstance.selectedTerms = term_default;
  }

  lengthGetCampaign(campaign: any) {
    if (campaign && typeof campaign === 'object') {
      return Object.keys(campaign).length;
    }
    return 0; // Devuelve 0 si `campaign` es null o no es
  }

  /* Convertir un titulo en un path url */
  convertTitleInPath(title: string): string {
    // Convertir el texto a minúsculas
    const textoMin = title.toLowerCase();
    // Reemplazar caracteres especiales y tildes por sus equivalentes sin ellos
    const caracteresEspeciales: any = {
      á: 'a',
      é: 'e',
      í: 'i',
      ó: 'o',
      ú: 'u',
      à: 'a',
      è: 'e',
      ì: 'i',
      ò: 'o',
      ù: 'u',
      ä: 'a',
      ë: 'e',
      ï: 'i',
      ö: 'o',
      ü: 'u',
      ñ: 'n',
      ç: 'c',
      ß: 'ss',
    };
    let textoSinEspeciales = '';
    for (let i = 0; i < textoMin.length; i++) {
      const caracter = textoMin.charAt(i);
      const caracterSinEspecial = caracteresEspeciales[caracter] || caracter;
      textoSinEspeciales += caracterSinEspecial;
    }
    // Eliminar el apóstrofe solo si está seguido de una "s" y precedido por una letra o número
    const textoSinApostrofe = textoSinEspeciales.replace('’s', 's').replace('?', '').replace('¿', '').replace(',', '');

    // Reemplazar los espacios en blanco por guiones
    const textoGuiones = textoSinApostrofe.replace(/\s+/g, '-');

    // Retornar el resultado
    return textoGuiones;
  }
  /* Detectar el modo de resoluciòn */
  detectScreenMode() {
    let resp;
    /* Dependiendo de la vista responsive, cargará cierta div padre de la vista */
    this.breakpointObserver.observe(['(min-width: 770px)']).subscribe((state: BreakpointState) => {
      if (state.matches) {
        //Entonces si el width es superior a 767, será la versión Desktop mostrando su propio diseño
        resp = true;
      } else {
        //Entonces si el width es inferio a 767, será la versión Mobile mostrando su propio diseño
        resp = false;
      }
    });

    return resp;
  }
  /* Obtener informaciòn del componente en archivos */
  GetSendInformationComponent_Files(): Promise<InformacionComponentesFiles> {
    return new Promise((resolve) => {
      this.notificacion_components.receiveNotificacion.subscribe((data: any) => {
        if (data.api === 'send_information_to_componentes_process') {
          let body_income_support_uploaded = data?.data?.extra?.income_support_uploaded || false;
          let body_document_id_saved = data?.data?.identity_validation?.data?.document_id_saved || false;

          let resp: InformacionComponentesFiles = {
            bank_name: data.data.bank_name,
            has_restrictions: data.data.extra.has_restrictions,
            size_file: 0,
            sizeText: '',
            bank_id: data.data.bank_id,
            document_support_uploaded: body_income_support_uploaded,
            document_id_saved: body_document_id_saved,
          };

          if (typeof resp.has_restrictions === 'undefined') {
            resp.has_restrictions = false;
          }

          if (resp.bank_name === this.globals_var.FLAMINGO || resp.bank_name === this.globals_var.COLTEFINANCIERA) {
            resp.size_file = 20000000;
            resp.sizeText = '20MB';
          } else {
            resp.size_file = 20000000;
            resp.sizeText = '20MB';
          }

          resolve(resp);
        }
      });
    });
  }

  structureFileProcess(process: GetProcessInterface): InformacionComponentesFiles {
    let body_income_support_uploaded = process.extra.income_support_uploaded || false;
    let body_document_id_saved = process?.identity_validation?.data?.document_id_saved || false;

    let resp: InformacionComponentesFiles = {
      bank_id: process.bank_id,
      bank_name: process.bank_name || '',
      has_restrictions: process.extra.has_restrictions || false,
      size_file: 0,
      sizeText: '',
      document_support_uploaded: body_income_support_uploaded,
      document_id_saved: body_document_id_saved,
    };

    if (typeof resp.has_restrictions === 'undefined') {
      resp.has_restrictions = false;
    }

    if (resp.bank_name === this.globals_var.FLAMINGO || resp.bank_name === this.globals_var.COLTEFINANCIERA) {
      resp.size_file = 20000000;
      resp.sizeText = '20MB';
    } else {
      resp.size_file = 20000000;
      resp.sizeText = '20MB';
    }

    return resp;
  }

  differenceDays(date_expiration: any): number {
    const fecha_actual = new Date();
    const fecha_expiration = new Date(date_expiration);

    // Convertir ambas fechas a milisegundos
    const tiempo_actual = fecha_actual.getTime();
    const tiempo_expiration = fecha_expiration.getTime();

    // Calcular la diferencia en milisegundos
    const diferencia_ms = tiempo_expiration - tiempo_actual;

    // Convertir la diferencia de milisegundos a días
    const diferencia_dias = Math.ceil(diferencia_ms / (1000 * 60 * 60 * 24));

    return diferencia_dias;
  }

  getRangeDate(dateFun: any, show: any): string {
    const date = new Date(dateFun);
    const day = date.getDate();
    const month = date.toLocaleString('es-ES', { month: 'short' });
    const year = date.getFullYear();

    if (show == 1) {
      return String(day);
    }
    if (show == 2) {
      return month;
    }
    if (show == 3) {
      return String(year);
    }

    return '';
  }

  //toma un número grande y lo convierte en una cadena abreviada.
  abbreviateLargeNumberToString(number: number, fraction: number) {
    if (isPlatformBrowser(this.platformId) && number !== undefined && number !== null) {
      let newValue: string = number.toString();
      if (number >= 1000) {
        // Definimos un arreglo de rangos de conversión
        const ranges = [
          { divider: 1, suffix: '' },
          { divider: 1e3, suffix: 'K' },
          { divider: 1e6, suffix: 'M' },
          { divider: 1e9, suffix: 'B' },
          { divider: 1e12, suffix: 'T' },
          { divider: 1e15, suffix: 'P' },
          { divider: 1e18, suffix: 'E' },
        ];

        // Inicializamos el índice en 0
        let index = 0;

        // Encontramos el rango adecuado para el número
        while (index < ranges.length - 1 && Math.abs(number) >= ranges[index + 1].divider) {
          index++;
        }

        // Redondeamos el valor dividiéndolo por el divisor del rango
        const roundedValue = (number / ranges[index].divider).toFixed(fraction);

        // Si el valor redondeado termina en ".0", eliminamos la parte decimal
        if (roundedValue.endsWith('.0')) {
          newValue = Math.floor(number / ranges[index].divider) + ranges[index].suffix;
        } else {
          // En caso contrario, conservamos la parte fraccional y el sufijo del rango
          newValue = roundedValue + ranges[index].suffix;
        }
      }
      // Manejamos el caso de 'NaN'
      if (newValue === 'NaN') {
        newValue = '***';
      }
      return newValue;
    }
    return;
  }

  investmentProfit(amount: any, roi: any) {
    let value: any = parseInt(roi) - parseInt(amount);
    if (Number.isNaN(value)) {
      value = '***';
    }
    return value;
  }

  formatteDateCDTs(dateString: string): string {
    // Separar la fecha en partes
    const [year, month, day] = dateString.split('-');

    // Crear un array con los nombres de los meses
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const monthIndex = parseInt(month, 10) - 1; // Convertir el mes en índice (0 basado)

    // Formatear la salida con HTML
    return `<span>${parseInt(day, 10)} de <b>${monthNames[monthIndex]}</b> de <b>${year}</b></span>`;
  }

  formatteDateToStringCompleted(date: Date | string) {
    const daysOfWeek = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const months = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ];

    let currentDate: Date;

    if (typeof date === 'string') {
      // Si es una cadena, intenta convertirla a un objeto Date usando la zona horaria UTC
      currentDate = new Date(date + 'T00:00:00Z');

      if (isNaN(currentDate.getTime())) {
        return 'Fecha no válida';
      }

      const dayOfWeek = daysOfWeek[currentDate.getUTCDay()];
      const dayOfMonth = currentDate.getUTCDate().toString().padStart(2, '0');
      const month = months[currentDate.getUTCMonth()];
      const year = currentDate.getUTCFullYear();
      return `${dayOfWeek}, ${dayOfMonth} de ${month} del ${year}`;
    } else {
      // Si ya es un objeto Date, usar hora local en lugar de UTC
      currentDate = date;

      if (isNaN(currentDate.getTime())) {
        return 'Fecha no válida';
      }

      const dayOfWeek = daysOfWeek[currentDate.getDay()];
      const dayOfMonth = currentDate.getDate().toString().padStart(2, '0');
      const month = months[currentDate.getMonth()];
      const year = currentDate.getFullYear();
      return `${dayOfWeek}, ${dayOfMonth} de ${month} del ${year}`;
    }
  }

  formatDateWithTimeIncluded(fecha: Date | string) {
    if (isPlatformBrowser(this.platformId) && fecha !== undefined && fecha !== null) {
      const date = new Date(fecha);

      // Restar 5 horas a la hora
      date.setHours(date.getHours() - 5);

      const day = date.getDate();
      const monthNames = [
        'Enero',
        'Febrero',
        'Marzo',
        'Abril',
        'Mayo',
        'Junio',
        'Julio',
        'Agosto',
        'Septiembre',
        'Octubre',
        'Noviembre',
        'Diciembre',
      ];
      const monthIndex = date.getMonth();
      const year = date.getFullYear();
      const hour = date.getHours();
      const minute = date.getMinutes();
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;

      return `${monthNames[monthIndex]} ${day} del ${year} - ${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
    }
    return '';
  }

  getQueryParam(query: any) {
    if (isPlatformBrowser(this.platformId)) {
      var url_search = window.location.search;
      var querys_params = new URLSearchParams(url_search);
      var param_resp = querys_params.get(query);
      return param_resp;
    }
    return;
  }

  convertNumberToText(number: any): string {
    const UNIDADES = ['', 'Uno', 'Dos', 'Tres', 'Cuatro', 'Cinco', 'Seis', 'Siete', 'Ocho', 'Nueve'];
    const DECENAS = [
      '',
      'Diez',
      'Veinte',
      'Treinta',
      'Cuarenta',
      'Cincuenta',
      'Sesenta',
      'Setenta',
      'Ochenta',
      'Noventa',
    ];
    const CENTENAS = [
      '',
      'Ciento',
      'Doscientos',
      'Trescientos',
      'Cuatrocientos',
      'Quinientos',
      'Seiscientos',
      'Setecientos',
      'Ochocientos',
      'Novecientos',
    ];
    const GRANDES_NUMEROS = [
      { limite: 1e12, singular: 'Billón', plural: 'Billones' },
      { limite: 1e9, singular: 'Mil millones', plural: 'Mil millones' },
      { limite: 1e6, singular: 'Millón', plural: 'Millones' },
    ];

    const convertirNumero = (num: number): string => {
      if (num === 0) {
        return 'Cero';
      }

      let palabras = '';

      if (num >= 100) {
        palabras += CENTENAS[Math.floor(num / 100)] + ' ';
        num %= 100;
      }

      if (num >= 10 && num < 20) {
        return palabras + DECENAS[num - 10];
      } else if (num >= 20) {
        palabras += DECENAS[Math.floor(num / 10)] + ' ';
        num %= 10;
      }

      if (num > 0) {
        palabras += UNIDADES[num];
      }

      return palabras.trim();
    };

    const convertirNumeroCompleto = (num: number): string => {
      if (num === 0) {
        return 'Cero';
      }

      let palabras = '';

      for (const grande of GRANDES_NUMEROS) {
        if (num >= grande.limite) {
          const parteEntera = Math.floor(num / grande.limite);
          palabras +=
            convertirNumeroCompleto(parteEntera) + ' ' + (parteEntera > 1 ? grande.plural : grande.singular) + ' ';
          num %= grande.limite;
        }
      }

      if (num >= 1000) {
        const miles = Math.floor(num / 1000);
        palabras += convertirNumeroCompleto(miles) + ' mil ';
        num %= 1000;
      }

      if (num > 0) {
        palabras += convertirNumero(num);
      }

      return palabras.trim();
    };

    const montoATexto = parseInt(number, 10);
    return convertirNumeroCompleto(montoATexto);
  }

  calculateDays(dateLimitd: Date, dateCurrent: Date) {
    const oneDayMs = 24 * 60 * 60 * 1000;
    const timeDiff = dateLimitd.getTime() - dateCurrent.getTime();
    return Math.ceil(timeDiff / oneDayMs);
  }

  thousandsSeparator(value: any) {
    if (isPlatformBrowser(this.platformId) && value !== undefined && value !== null) {
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
  }

  redondearAmount(amount: any) {
    return Math.round(amount);
  }

  _CloseModal(element: any) {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        let name: HTMLElement;
        if (element) {
          name = document.getElementsByClassName(element)[0] as HTMLElement;
          return name.click();
        }
      }, 200);
    }
  }

  //Convertir abreviatura de los montos
  convertNumberToShortString(n: any) {
    if (n < 1e3) return n;
    if (n >= 1e3 && n < 1e6) return +(n / 1e3).toFixed(1) + 'K';
    if (n >= 1e6 && n < 1e9) return +(n / 1e6).toFixed(1) + 'M';
    if (n >= 1e9 && n < 1e12) return +(n / 1e9).toFixed(1) + 'B';
    if (n >= 1e12) return +(n / 1e12).toFixed(1) + 'T';

    return n;
  }

  updateCookies_Simulation(fullname: any, phone: any, amountValue: any) {
    this.cookies.save('amount', amountValue, 1);
    this.cookies.save('fullname', fullname, 1);
    this.cookies.save('phone-full', phone.e164Number, 1);
    this.cookies.save('phone-number', phone.number, 1);
  }

  scrollAnimation(target: string, duration: number, offset: number) {
    if (isPlatformBrowser(this.platformId)) {
      const targetElement: any = document.querySelector(target);
      const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition - offset;
      let startTime: any = null;

      function animation(currentTime: number) {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const run = easing(timeElapsed, startPosition, distance, duration);
        window.scrollTo(0, run);
        if (timeElapsed < duration) requestAnimationFrame(animation);
      }

      function easing(t: number, b: number, c: number, d: number) {
        t /= d / 2;
        if (t < 1) return (c / 2) * t * t + b;
        t--;
        return (-c / 2) * (t * (t - 2) - 1) + b;
      }

      requestAnimationFrame(animation);
    }
  }

  scrollTop(): void {
    if (isPlatformBrowser(this.platformId)) {
      const currentScrollTop = document.documentElement.scrollTop;

      if (currentScrollTop > 20) {
        const target = '#aplication-tubo-mcdt';
        const duration = 800;
        const offset = 10;

        setTimeout(() => {
          this.scrollAnimation(target, duration, offset);
        }, 500);
      }
    }
  }

  scrollToNewsMejorCDT(id: any) {
    const target = id;
    const duration = 1000;
    const offset = 60;
    this.smoothScroll(target, duration, offset);
  }

  isCampaignMonth() {
    let date_today: any = new Date();
    let date_expired: any = '2024-09-26T08:00:00';
    let campaign_days = new Date(date_expired);

    if (date_today < campaign_days) {
      let time_difference = date_expired - date_today;
      let days_remaining = Math.ceil(time_difference / (1000 * 60 * 60 * 24));
      let notificacion_params_campaign_month = {
        date_expired: date_expired,
        last_day: false,
        background: '/images/web/campaign/cupido/cdt-cupido-campaign-agoto.png',
        logo: 'cdt-cupido-y-bluedays.png',
        logo_full: '/images/web/slider/cdt-cupido-y-bluedays.png',
        title: '¡Este mes, el amor y la inversión a largo plazo, se premian!',
        description:
          'Para el amor: al abrir un CDT por 1 millón de pesos, participa por una noche grátis en un glamping. Y para la inversión: desde $20M tenemos una tasa especial para ti.',
        title_button: 'Ver promoción',
        router_link: '/cdt-cupido',
        campaign: 'cupido',
      };

      if (days_remaining <= 3) {
        notificacion_params_campaign_month.last_day = true;
      }

      this.notificacion_components.sendNotificacion({
        api: 'send_information_campaign_month_active',
        notificacion_params_campaign_month,
      });
      return true;
    } else {
      this.notificacion_components.sendNotificacion({
        api: 'send_information_campaign_month_deactive',
        router: this.router.url.replace(/\?.*$/, ''),
      });
      return false;
    }
  }

  isCampaignMonthReturnBool() {
    let date_today: any = new Date();
    let date_expired: any = '2024-09-26T08:00:00';
    let campaign_days = new Date(date_expired);

    if (date_today < campaign_days) {
      return true;
    } else {
      return false;
    }
  }

  isViewApp() {
    if (this.cookies.check('view') || this.hasQueryParamSnapshot('view')) {
      return true;
    }
    return false;
  }

  replaceNameBanks(bankname: string) {
    if (bankname) {
      let bankname_replace = bankname
        .replace(this.globals_var.MIBANCO, 'Mibanco')
        .replace(this.globals_var.FINANCIERA_DANN_REGIONAL, this.globals_var.DANNREGIONAL)
        .replace('Banco Bogotá', this.globals_var.BOGOTA)
        .replace('Bogotá', 'Banco de Bogotá')
        .replace('Banco Davivienda', this.globals_var.DAVIVIENDA)
        .replace('Bancamía', this.globals_var.BANCAMIA)
        .replace('Coomeva', 'Bancoomeva')
        .replace('Mi Banco', this.globals_var.MIBANCO)
        .replace('Mibanco', this.globals_var.MIBANCO)
        .replace('Colpatria', this.globals_var.DAVIBANK);

      return bankname_replace;
    }

    return '';
  }

  /**
   * Determina la categoría de un usuario basándose en el estado y el valor total de sus CDTs activos.
   * Las categorías son:
   * - 'Primerizo': No tiene CDTs activos.
   * - 'Ahorrador': Tiene exactamente 1 CDT activo.
   * - 'Avanzado': Tiene 2 o más CDTs activos.
   * - 'Experto': Tiene uno o varios CDTs activos y la suma total de sus montos es igual o superior a 50 millones.
   *
   * @param processes Representa todos los procesos actuales del usuario
   * @returns La categoría del usuario como una cadena de texto ('Primerizo', 'Ahorrador', 'Avanzado', 'Experto').
   */

  getTagLevelIndicator(processes: any) {
    const CDT_ACTIVE_STATUSES = ['DECEVAL-EMITIDO-Y-RECIBIDO', 'DECEVAL-ENVIADO-AL-CLIENTE'];

    if (!processes || processes.length === 0) {
      return 'Primerizo';
    }

    const activeCdts = processes.filter((process: { process_status: string }) =>
      CDT_ACTIVE_STATUSES.includes(process.process_status),
    );
    const numberOfActiveCdts = activeCdts.length;
    const totalAmountActiveCdts = activeCdts.reduce((sum: any, cdt: { amount: any }) => sum + cdt.amount, 0);
    const MILLONES_50 = 50000000;

    if (numberOfActiveCdts === 0) {
      return 'Primerizo';
    }

    if (totalAmountActiveCdts >= MILLONES_50) {
      return 'Experto';
    }

    if (numberOfActiveCdts >= 2) {
      return 'Avanzado';
    }

    if (numberOfActiveCdts === 1) {
      return 'Ahorrador';
    }

    return 'Primerizo';
  }

  /* ----------------------------------------------- */

  removeObjectsCompletedSatus(processes: any): any {
    // Si no es un array, retornar inmediatamente un objeto vacío
    if (!processes || !Array.isArray(processes)) {
      return {};
    }

    const allowedStatus = [
      'LEAD-SIN-FORMULARIO',
      'CLIENTE-SUBIENDO-DOCUMENTOS',
      'DOCUMENTOS-SUBIDOS-VALIDANDO-PARA-FIRMA',
      'ENVIADOS-DOCUMENTOS-A-FIRMA',
      'DOCUMENTOS-FIRMADOS-ESPERANDO-SARLAF',
      'ESPERANDO-DEPOSITO',
      'PENDIENTE-DE-REVISION',
      'ENVIADO-AL-BANCO-PARA-CREACION',
    ];

    return processes.filter((process: { process_status: string }) => {
      return allowedStatus.includes(process.process_status);
    });
  }

  removeAccents(text: string): string {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  scrollToSection(id: string) {
    if (isPlatformBrowser(this.platformId)) {
      const section = document.getElementById(id);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  smoothScroll(target: string, duration: number, offset: number) {
    if (isPlatformBrowser(this.platformId)) {
      const targetElement: any = document.querySelector(target);

      if (!targetElement) {
        return;
      }

      const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition - offset;
      let startTime: any = null;

      function animation(currentTime: number) {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const run = easing(timeElapsed, startPosition, distance, duration);
        window.scrollTo(0, run);
        if (timeElapsed < duration) requestAnimationFrame(animation);
      }

      function easing(t: number, b: number, c: number, d: number) {
        t /= d / 2;
        if (t < 1) return (c / 2) * t * t + b;
        t--;
        return (-c / 2) * (t * (t - 2) - 1) + b;
      }

      requestAnimationFrame(animation);
    }
  }

  deleteSemicolons(value: string | number): string {
    if (isPlatformBrowser(this.platformId) && value !== undefined && value !== null) {
      if (typeof value === 'number') {
        value = value.toString();
      }
      return value.replace(/[.,]/g, '');
    }
    return '';
  }

  removeElementsWithZeroRateRoi(list: any[]): any[] {
    return list.filter((element) => element.rate !== 0 || element.roi !== 0);
  }

  isAlliedBank(bank: string) {
    const bankWithoutAccents = this.replaceNameBanks(this.removeAccents(bank));
    if (alliedBank.includes(bankWithoutAccents)) {
      return true;
    }
    return false;
  }

  openCDT(response: openCDT): void {
    const { bank, term, options, turbo, amount } = response;

    const modalData = {
      bank: this.replaceNameBanks(bank),
      place: term,
      data: this.removeElementsWithZeroRateRoi(options),
      turbo: turbo,
      step: 2,
      amount: amount,
    };

    if (response.ga) {
      this._gta_events.triggerGTMEvent(response.ga, 'AppMejorCDTComponent');
    }

    this._gta_events.triggerGTMEvent('**ga_abre_cdt_general_old_new', 'AppMejorCDTComponent');
    this._gta_events.triggerGTMEvent('ga_simulaold_btn_abre_cdt_general', 'AppMejorCDTComponent');
    this._gta_events.triggerGTMEvent(`ga_simulaold_btn_abre_cdt_${response.bank_id}`, 'AppMejorCDTComponent');

    if (this.isAlliedBank(bank)) {
      this.openCDTModal(modalData);
    } else {
      this.openModalNotAllied(bank);
    }
  }

  openCDTModal(data: any): void {
    const modal = this.modal.open(CheckParametersComponent, {
      centered: true,
      size: 'md options_CheckParametros_ModalMobile',
      backdrop: 'static',
    });
    modal.componentInstance.CheckParametersDataModal = data;
  }

  openModalNotAllied(bankname: any): void {
    const modal = this.modal.open(BankNotAlliedComponent, {
      centered: true,
    });
    modal.componentInstance.bankname_options = bankname;
  }

  validatePhone(inputPhone: any): { valid: boolean; phoneNumber: string; phoneFull: string; phonePrefix: string } {
    return {
      valid: inputPhone.validate,
      phoneNumber: inputPhone.phone_number,
      phoneFull: this.deleteSpace(inputPhone.phone_full),
      phonePrefix: this.deleteSpace(inputPhone.prefix),
    };
  }

  cleanAmount(amount: string): string {
    return amount?.split('.').join('');
  }

  deleteSpace(text: string): string {
    if (text) {
      return text.replace(/\s+/g, '');
    }
    return text;
  }

  orderBestBank_WidgetRate(order_term: number, results: any[]): WidgetRate[] {
    // Filtra los resultados que coinciden con el order_term
    const results_filter = results.filter((response) => response.term === order_term);
    // Ordena los resultados filtrados por la propiedad rate de mayor a menor
    const results_order = results_filter.sort((a, b) => b.rate - a.rate);
    // Retorna los primeros 3 resultados (o menos si no hay suficientes)
    return results_order.slice(0, 3);
  }

  getBestBank_SimulateInvestment(order_term: number, results: any[]): WidgetRate[] {
    // Filtra los resultados para el plazo de 360
    const results_for_term = results.filter((response) => response.term === order_term);
    if (results_for_term.length === 0) {
      // No hay resultados para el plazo de 360
      return [];
    }
    // Ordena los resultados para el plazo de 360 por la propiedad rate de mayor a menor
    const best_results_term = results_for_term.reduce((max: any, current: any) =>
      current.rate > max.rate ? current : max,
    );
    return best_results_term.bank_label;
  }

  reverseOptions(options: any) {
    return options.reverse();
  }

  onlyNumberKey(event: any) {
    // Obtener el carácter ingresado
    const inputChar = String.fromCharCode(event.charCode);

    // Utilizar una expresión regular para permitir solo caracteres numéricos y la tecla de retroceso
    const pattern = /^[0-9\b]+$/;
    if (!pattern.test(inputChar)) {
      event.preventDefault();
    }
  }

  confirmRefProcessExit(event: MouseEvent): void {
    if (isPlatformBrowser(this.platformId)) {
      if (this.globals_var.PATHS_CONFIRM_PROCESS_EXIT.includes(window.location.pathname)) {
        event.preventDefault();
        this.openAlertConfirmation(this.confirmProcessExitModaltext).then((result: boolean) => {
          if (!result) {
            window.location.href = (event.target as HTMLAnchorElement).href;
          }
        });
      }
    }
  }

  confirmNavigationProcessExit(route: string): void {
    if (isPlatformBrowser(this.platformId)) {
      const isExternalLink = route.startsWith('http://') || route.startsWith('https://');

      if (this.globals_var.PATHS_CONFIRM_PROCESS_EXIT.includes(window.location.pathname)) {
        this.openAlertConfirmation(this.confirmProcessExitModaltext).then((result: boolean) => {
          if (!result) {
            if (isExternalLink) {
              window.location.href = route;
            } else {
              this.router.navigateByUrl(route);
            }
          }
        });
      } else {
        if (isExternalLink) {
          window.location.href = route;
        } else {
          this.router.navigateByUrl(route);
        }
      }
    }
  }

  openAlertConfirmation(modalData: { title: string; message: string }): Promise<boolean> {
    console.log('openAlertConfirmation modalData:', modalData);

    return new Promise<boolean>((resolve) => {
      const modalRef = this.modal.open(AlertConfirmComponent, { centered: true });
      modalRef.componentInstance.modalData = modalData;
      modalRef.componentInstance.getResult().subscribe((result: boolean) => {
        resolve(result);
      });
    });
  }

  removeDuplicateCookies(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const allCookies = this.cookies.all();
        const cookieMap = new Map();

        allCookies.forEach((cookie) => {
          if (cookieMap.has(cookie.name)) {
            const existingCookie = cookieMap.get(cookie.name);
            document.cookie = `${cookie.name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=.mejorcdt.com; path=/`;
          }
        });
      } catch (error) {
        console.log('Error remove cookies duplicate: ', error);
      }
    }
  }

  getTransparentColor(color: string | undefined): string {
    if (!color) {
      console.warn('Color no definido o inválido:', color);
      return 'transparent'; // O un valor por defecto
    }
    if (color.startsWith('#')) {
      return this.hexToRgba(color, 0.6);
    }
    if (color.startsWith('rgb')) {
      return this.rgbToRgba(color, 0.6);
    }
    return color;
  }

  hexToRgba(hex: string, alpha: number): string {
    let r, g, b;

    // Elimina el hash si está presente
    hex = hex.replace('#', '');

    // Convierte valores hexadecimales cortos (e.g., #FFF) a completos (e.g., #FFFFFF)
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((char) => char + char)
        .join('');
    }

    // Extrae los valores de los componentes RGB
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  rgbToRgba(rgb: string, alpha: number): string {
    // Extrae los valores de RGB del string
    const rgbValues = rgb.match(/\d+/g);
    if (!rgbValues || rgbValues.length < 3) return rgb; // Devuelve el color original si no es válido

    const [r, g, b] = rgbValues.map(Number);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  getPathWithoutQueryParams(): string {
    // Usar el servicio Router para obtener la URL actual
    const fullUrl = this.router.url;
    // Separar la URL por el signo de '?' (query parameters) y tomar solo la primera parte (path)
    const path = fullUrl.split('?')[0];
    return path;
  }

  removeHtmlTags(inputString: string) {
    return inputString.replace(/<[^>]+>/g, '');
  }

  openModalPremiarFidelidad(): void {
    if (isPlatformBrowser(this.platformId)) {
      const has_modal_been_opened = this.cookies.check('mpf');
      if (!has_modal_been_opened) {
        setTimeout(() => {
          const modal = this.modal.open(ModalPremiarFidelidadComponent, {
            centered: true,
            size: 'lg paddingModal options_CheckParametros_ModalMobile',
            backdrop: 'static',
          });
          modal.componentInstance.data = {};
        }, 2000);
      }
    }
  }

  extractPhonePrefix(phoneNumber: string): string {
    if (!phoneNumber) {
      return '';
    }
    const match = phoneNumber.match(/^(\+\s?\d+)\s/);
    return match ? match[1].replace(/\s/g, '') : '';
  }

  async loadWhiteLabelData(brand: string): Promise<any> {
    const files = ['footer.json', 'header.json', 'seo.json', 'tubo.json'];
    const data: any = {};
    const brand_mcdt = 'mcdt';

    if (brand != brand_mcdt) {
      await Promise.all(
        files.map(async (file) => {
          const path = `assets/brands/${brand}/json/${file}`;
          try {
            const response = await fetch(path);
            if (!response.ok) {
              throw new Error(`Error al cargar el archivo ${file}: ${response.statusText}`);
            }
            data[file.replace('.json', '')] = await response.json();
          } catch (error) {
            console.error(`Error al cargar el JSON de ${file} - ${brand}:`, error);
            data[file.replace('.json', '')] = null;
          }
        }),
      );
    }

    return data;
  }

  /**
   * Verifica SÍNCRONAMENTE si un query parameter específico existía en la URL
   * en el momento de la inicialización del componente.
   *
   * IMPORTANTE: No detecta cambios posteriores en los query parameters.
   *
   * @param paramName - El nombre del query parameter a verificar.
   * @returns boolean - True si el parámetro existía, false en caso contrario.
   */
  hasQueryParamSnapshot(paramName: string): boolean {
    return this.route.snapshot.queryParams.hasOwnProperty(paramName);
  }
}
